'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, ShieldCheck } from 'lucide-react';

interface ParentFormData {
  email: string;
  password: string;
  twoFACode: string;
}

export default function ParentLoginForm({ isSignUp }: { isSignUp: boolean }) {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'credentials' | '2fa'>('credentials');
  const router = useRouter();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ParentFormData>({ defaultValues: { email: '', password: '', twoFACode: '' } });

  const onSubmit = async (_data: ParentFormData) => {
    if (step === 'credentials' && !isSignUp) {
      setLoading(true);
      await new Promise((r) => setTimeout(r, 800));
      setLoading(false);
      setStep('2fa');
      return;
    }
    setLoading(true);
    // BACKEND INTEGRATION: POST /api/auth/parent with email, password, twoFACode
    await new Promise((r) => setTimeout(r, 1200));
    setLoading(false);
    router.push('/parent-dashboard');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
      {step === 'credentials' ? (
        <>
          <div>
            <label htmlFor="parent-email" className="block text-sm font-semibold text-foreground mb-1.5">
              Email Address
            </label>
            <input
              id="parent-email"
              type="email"
              {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' } })}
              placeholder="david.osei@jarvondisu.edu"
              className="w-full px-4 py-2.5 bg-input border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring transition-all"
            />
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>}
          </div>

          <div>
            <label htmlFor="parent-password" className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
            <div className="relative">
              <input
                id="parent-password"
                type={showPassword ? 'text' : 'password'}
                {...register('password', { required: 'Password is required' })}
                placeholder="••••••••"
                className="w-full px-4 py-2.5 pr-10 bg-input border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password.message}</p>}
          </div>
        </>
      ) : (
        <div>
          <div className="flex items-center gap-2 mb-4 p-3 bg-sky-50 border border-sky-200 rounded-lg">
            <ShieldCheck size={16} className="text-sky-600 shrink-0" />
            <p className="text-sm text-sky-700">A 6-digit code has been sent to your email. Enter it below to continue.</p>
          </div>
          <label htmlFor="parent-2fa" className="block text-sm font-semibold text-foreground mb-1.5">
            Two-Factor Code
          </label>
          <p className="text-xs text-muted-foreground mb-2">Check your email for a 6-digit verification code.</p>
          <input
            id="parent-2fa"
            type="text"
            inputMode="numeric"
            maxLength={6}
            {...register('twoFACode', { required: '2FA code required', pattern: { value: /^\d{6}$/, message: 'Enter a 6-digit code' } })}
            placeholder="000000"
            className="w-full px-4 py-2.5 bg-input border border-border rounded-lg text-sm text-center font-mono tracking-[0.5em] text-foreground outline-none focus:ring-2 focus:ring-ring transition-all"
          />
          {errors.twoFACode && <p className="text-xs text-red-500 mt-1">{errors.twoFACode.message}</p>}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 py-3 bg-sky-600 text-white font-semibold rounded-lg btn-scale hover:bg-sky-700 transition-all disabled:opacity-60"
        style={{ minHeight: '44px' }}
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : null}
        {loading ? 'Verifying...' : step === 'credentials' ? (isSignUp ? 'Create Parent Account' : 'Continue') : 'View Child Dashboard'}
      </button>

      {step === '2fa' && (
        <button type="button" onClick={() => setStep('credentials')} className="w-full text-sm text-muted-foreground hover:text-foreground text-center">
          ← Back to login
        </button>
      )}
    </form>
  );
}